import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EmailComposeAttachment, EmailReplySendPayload } from '@elva/ui';
import { keys } from '@/api/queryKeys';
import { enrichSentAttachments } from '../adapters/enrichSentAttachments';
import {
  buildEmailReplyWirePayload,
  buildLegacyEmailReplyPayload,
  emailApi,
} from '../api/emailApi';
import { isEmailSyncEnabled, markEmailSyncUnavailable, shouldDisableEmailSync } from '../api/emailSync';
import type { EmailMessageDetail } from '../api/types';

function invalidateEmailThread(queryClient: ReturnType<typeof useQueryClient>, messageId: string) {
  void queryClient.invalidateQueries({ queryKey: keys.email.messages() });
  void queryClient.invalidateQueries({ queryKey: keys.email.message(messageId) });
}

function mergeThreadDetail(
  current: EmailMessageDetail | undefined,
  incoming: EmailMessageDetail,
): EmailMessageDetail {
  if (!current) return incoming;

  const mergedMessages = incoming.messages?.map((message) => {
    const prior = current.messages?.find((row) => row.id === message.id);
    if (!prior) return message;
    return {
      ...message,
      ...(prior.seen_by?.length ? { seen_by: prior.seen_by } : {}),
      ...(prior.seen_count != null ? { seen_count: prior.seen_count } : {}),
    };
  });

  return {
    ...current,
    ...incoming,
    ...(current.seen_by?.length ? { seen_by: current.seen_by } : {}),
    ...(current.seen_count != null ? { seen_count: current.seen_count } : {}),
    ...(mergedMessages ? { messages: mergedMessages } : {}),
  };
}

async function refreshThreadAfterSend(
  messageId: string,
  queryClient: ReturnType<typeof useQueryClient>,
  handles?: string[],
  files?: EmailComposeAttachment[],
) {
  try {
    if (isEmailSyncEnabled()) {
      try {
        await emailApi.syncThread(messageId);
      } catch (err) {
        if (shouldDisableEmailSync(err)) {
          markEmailSyncUnavailable();
        }
      }
    }

    const fresh = await emailApi.getLegacyThread(messageId);
    const enriched =
      handles?.length && files?.length
        ? enrichSentAttachments(fresh, handles, files)
        : fresh;
    queryClient.setQueryData(keys.email.message(messageId), (current: EmailMessageDetail | undefined) =>
      mergeThreadDetail(current, enriched),
    );
  } catch {
    // keep optimistic enriched cache when sync/detail refresh fails
  }
  void queryClient.invalidateQueries({ queryKey: keys.email.messages() });
}

type LegacySendResult = {
  detail: EmailMessageDetail;
  handles: string[];
  files: EmailComposeAttachment[];
};

export function useReplySend(messageId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: EmailReplySendPayload): Promise<LegacySendResult> => {
      const wire = await buildLegacyEmailReplyPayload(payload);
      const detail = await emailApi.sendLegacyReply(messageId, wire);
      return {
        detail,
        handles: wire.attachment_handles ?? [],
        files: payload.attachments ?? [],
      };
    },
    onSuccess: ({ detail, handles, files }) => {
      const enriched = enrichSentAttachments(detail, handles, files);
      queryClient.setQueryData(keys.email.message(messageId), (current: EmailMessageDetail | undefined) =>
        mergeThreadDetail(current, enriched),
      );
      void refreshThreadAfterSend(messageId, queryClient, handles, files);
    },
  });
}

export function useApproveDraft(messageId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload?: EmailReplySendPayload) => {
      if (payload?.attachments?.length) {
        const wire = await buildLegacyEmailReplyPayload(payload);
        return {
          detail: await emailApi.sendLegacyReply(messageId, wire),
          handles: wire.attachment_handles ?? [],
          files: payload.attachments ?? [],
        } satisfies LegacySendResult;
      }
      await emailApi.approveDraft(
        messageId,
        payload ? await buildEmailReplyWirePayload(payload) : undefined,
      );
      return null;
    },
    onSuccess: (data) => {
      if (data?.detail) {
        const enriched = enrichSentAttachments(data.detail, data.handles, data.files);
        queryClient.setQueryData(keys.email.message(messageId), (current: EmailMessageDetail | undefined) =>
          mergeThreadDetail(current, enriched),
        );
        void refreshThreadAfterSend(messageId, queryClient, data.handles, data.files);
        return;
      }
      void refreshThreadAfterSend(messageId, queryClient);
    },
  });
}

export function useMarkNoReply() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => emailApi.markNoReply(messageId),
    onSuccess: (_data, messageId) => invalidateEmailThread(queryClient, messageId),
  });
}

export function usePatchEmailMessage(messageId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (action: string) => emailApi.patchMessage(messageId, action),
    onSuccess: () => invalidateEmailThread(queryClient, messageId),
  });
}

export function useTranslateThread(messageId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (options?: { force?: boolean }) => {
      if (!messageId.trim()) {
        return Promise.reject(new Error('No thread selected'));
      }
      return emailApi.translateThread(messageId, options);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(keys.email.message(messageId), (current: EmailMessageDetail | undefined) =>
        mergeThreadDetail(current, data),
      );
      void queryClient.invalidateQueries({ queryKey: keys.email.messages() });
    },
  });
}
